import { Check, Loader2, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import type { ConversationEntry } from '@/domain/types';
import { useComposerDraftStore } from '@/state/composerDraftStore';

/**
 * Per-message delivery state for real DM sends (tv-isee, mockup artboard 4).
 * Buttons never send — they load the text back into the composer so a human
 * presses send (required for human-agent sends; avoids double sends).
 */
export default function DeliveryStatusChip({ entry }: { entry: ConversationEntry }) {
  const setDraft = useComposerDraftStore((s) => s.setDraft);
  if (entry.origin !== 'server' || entry.direction !== 'out' || !entry.deliveryStatus) return null;
  const who = entry.authorName ? ` · ${entry.authorName}` : '';
  const resend = (label: string) => (
    <button
      type="button"
      onClick={() => setDraft(entry.customerId, entry.body, entry.templateId, entry.depositId)}
      className="ml-2 rounded-md border border-blood px-2 py-0.5 text-[11px] font-semibold text-blood hover:bg-blood/10"
    >
      {label}
    </button>
  );

  const base = 'inline-flex items-center gap-1 text-[11px] font-semibold';
  switch (entry.deliveryStatus) {
    case 'sending':
      return (
        <span data-testid="delivery-chip" className={clsx(base, 'text-cream-soft')}>
          <Loader2 size={11} className="animate-spin" /> Sending…
        </span>
      );
    case 'sent':
      return (
        <span data-testid="delivery-chip" className={clsx(base, 'text-mint')}>
          <Check size={11} /> {entry.sendPath === 'human_agent' ? 'Sent as agent' : 'Sent'}{who}
        </span>
      );
    case 'failed':
      return (
        <span data-testid="delivery-chip" className={clsx(base, 'text-blood')}>
          {entry.deliveryError === 'window_closed' ? 'Not sent · reply window closed' : 'Not sent · Instagram refused it'}
          {resend(entry.deliveryError === 'window_closed' ? 'Send as agent instead' : 'Try again')}
        </span>
      );
    case 'unknown':
      return (
        <span data-testid="delivery-chip" className={clsx(base, 'text-orange')}>
          Unknown · check Instagram before resending
        </span>
      );
    case 'handoff_unconfirmed':
      return (
        <span data-testid="delivery-chip" className={clsx(base, 'text-cream-soft')}>
          <ExternalLink size={11} /> Sent via Instagram (not confirmed){who}
        </span>
      );
    default:
      return null;
  }
}
